// areaGeometricTools.js — Bespoke draw interactions for AREA fixed-geometry rules.
// AREA5, AREA7, AREA8, AREA11, AREA12, AREA17, AREA18, AREA21, AREA24, AREA25
//
// Each is a fixed-count point collector; rendering is handled by WebRenderer
// using the positional coordinate array.

import { DrawRules } from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import { registerDrawInteraction } from '../customDrawInteractions';
import { createFixedPointCollector } from './fixedPointCollector';
import { createMultiPointCollector } from './multiPointCollector';
import Feature from 'ol/Feature';
import { LineString, Circle as OLCircleGeom } from 'ol/geom';
import { PREVIEW_LINE_BLUE, PREVIEW_LINE_AMBER_DASHED } from './drawPreviewStyles';

function make3pt(drawRule) {
    return (map, opts) => createFixedPointCollector(map, { targetCount: 3, onFinish: opts.onFinish });
}

function make4pt(drawRule) {
    return (map, opts) => createFixedPointCollector(map, { targetCount: 4, onFinish: opts.onFinish });
}

registerDrawInteraction(DrawRules.AREA5,  make3pt(DrawRules.AREA5));
registerDrawInteraction(DrawRules.AREA7,  make3pt(DrawRules.AREA7));
registerDrawInteraction(DrawRules.AREA8,  make4pt(DrawRules.AREA8));
registerDrawInteraction(DrawRules.AREA11, make3pt(DrawRules.AREA11));
registerDrawInteraction(DrawRules.AREA12, make3pt(DrawRules.AREA12));
registerDrawInteraction(DrawRules.AREA17, make3pt(DrawRules.AREA17));
registerDrawInteraction(DrawRules.AREA18, make4pt(DrawRules.AREA18));
registerDrawInteraction(DrawRules.AREA21, make3pt(DrawRules.AREA21));
registerDrawInteraction(DrawRules.AREA24, make3pt(DrawRules.AREA24));
registerDrawInteraction(DrawRules.AREA25, make3pt(DrawRules.AREA25));

// ── AREA13: 2-pt straight + semicircle ────────────────────────────────────────
// P1 and P2 define a straight line; renderer appends a semicircle whose
// diameter equals |P1–P2|.  Sketch shows the line + a live semicircle arc.

const _A13_LINE = PREVIEW_LINE_BLUE;
const _A13_ARC  = PREVIEW_LINE_AMBER_DASHED;

registerDrawInteraction(DrawRules.AREA13, (map, opts) => createMultiPointCollector(map, {
    minCount: 2,
    maxCount: 2,
    onFinish: opts.onFinish,
    // Both points are plain corners with no tip/rear or width meaning (see this file's header
    // comment) — both stay 'anchor'.
    roleForIndex: () => 'anchor',
    styleResolver: (f) => (f.get('_t') === 'arc' ? _A13_ARC : _A13_LINE),
    onSketchUpdate: ({ previewSource, collected3857, cursorCoord3857 }) => {
        const pts = cursorCoord3857 ? [...collected3857, cursorCoord3857] : collected3857;
        if (pts.length >= 2) {
            const [a, b] = pts;
            // Line segment
            const lf = new Feature(new LineString([a, b]));
            lf.set('_t', 'line');
            previewSource.addFeature(lf);

            // Semicircle arc indicator: circle centered at midpoint, radius = ½|AB|
            const mx = (a[0] + b[0]) / 2;
            const my = (a[1] + b[1]) / 2;
            const dx = b[0] - a[0];
            const dy = b[1] - a[1];
            const radius = Math.sqrt(dx * dx + dy * dy) / 2;
            if (radius > 0) {
                const cf = new Feature(new OLCircleGeom([mx, my], radius));
                cf.set('_t', 'arc');
                previewSource.addFeature(cf);
            }
        }
    },
}));

